import { useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useMutation } from '@apollo/client/react';
import type { ResultOf } from '@graphql-typed-document-node/core';
import { Mark } from '~components/Logo';
import { LoginPanel } from '~components/LoginPanel';
import type { AuthSession } from '~utils/auth';
import { SiteFooter } from '~components/SiteFooter';
import { shared } from '~styles';
import { styles } from './styles';
import { DELETE_ACCOUNT } from './operations';

// Google Play's account-deletion policy requires a web URL where users can
// delete their account without reinstalling the app; this page is the URL
// declared in the Play Data safety form. Sign in with any of the app's
// methods, confirm, and the account enters the same 30-day soft-delete the
// in-app path uses.
export function DeleteAccount() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [deletedAt, setDeletedAt] = useState<string | null>(null);
  return <>
      <main {...stylex.props(styles.container)}>
        <Mark size={56} />
        <h1 {...stylex.props(styles.title)}>Delete your account</h1>
        {!session && <>
            <p {...stylex.props(shared.muted, styles.lead)}>
              Sign in to permanently delete your Tribys account and its data.
              You don&apos;t need the app installed.
            </p>
            <LoginPanel onLogin={setSession} />
          </>}
        {session && !deletedAt && (session.user.deleteAt ? <AlreadyScheduled deleteAt={session.user.deleteAt} /> : <Confirm session={session} onDeleted={setDeletedAt} onCancel={() => setSession(null)} />)}
        {deletedAt && <Done deleteAt={deletedAt} />}
      </main>
      <SiteFooter />
    </>;
}
function Confirm({
  session,
  onDeleted,
  onCancel
}: {
  session: AuthSession;
  onDeleted: (deleteAt: string) => void;
  onCancel: () => void;
}) {
  const [deleteAccount, {
    loading,
    error
  }] = useMutation(DELETE_ACCOUNT, {
    context: {
      headers: {
        authorization: `Bearer ${session.token}`
      }
    }
  });
  const {
    user
  } = session;
  return <div {...stylex.props(styles.confirm)}>
      <p {...stylex.props(styles.identity)}>
        Signed in as <strong>@{user.username}</strong>
        {user.email?.value ? ` (${user.email.value})` : ''}
      </p>
      {!user.hasFinishedOnboarding && <p {...stylex.props(styles.warning)}>
          This account looks brand-new. If you meant to delete an existing
          account, you may have signed in with a different method than the one
          you originally used — go back and try another one.
        </p>}
      <div {...stylex.props(shared.card, styles.disclosure)}>
        <p {...stylex.props(styles.disclosureText)}>Deleting your account removes:</p>
        <ul {...stylex.props(styles.list)}>
          <li>Your profile, photos and interests</li>
          <li>Your messages and chat history</li>
          <li>Your tribys, spots and connections</li>
          <li>Any active subscription entitlements (app-store billing is
            cancelled separately through Apple or Google)
          </li>
        </ul>
        <p {...stylex.props(styles.disclosureText)}>
          Deletion is scheduled immediately and becomes permanent after a
          30-day grace period. Signing back in within those 30 days lets you
          restore the account; after that, your data is permanently erased.
        </p>
      </div>
      <button type="button" {...stylex.props(styles.dangerButton)} onClick={() => confirmDeletion({
      deleteAccount,
      onDeleted
    })} disabled={loading}>
        {loading ? 'Deleting…' : 'Permanently delete my account'}
      </button>
      <button type="button" {...stylex.props(styles.cancelButton)} onClick={onCancel}>
        Cancel and sign out
      </button>
      {error && <p {...stylex.props(styles.errorText)} role="alert">
          Something went wrong. Please try again.
        </p>}
    </div>;
}
function AlreadyScheduled({
  deleteAt
}: {
  deleteAt: string;
}) {
  return <p {...stylex.props(shared.muted, styles.lead)}>
      This account is already scheduled for deletion on {formatDate(deleteAt)}.
      To keep it instead, open the Tribys app and sign in before that date.
    </p>;
}
function Done({
  deleteAt
}: {
  deleteAt: string;
}) {
  return <div {...stylex.props(styles.confirm)}>
      <p {...stylex.props(styles.identity)}>Your account is scheduled for deletion.</p>
      <p {...stylex.props(shared.muted, styles.lead)}>
        It will be permanently removed on {formatDate(deleteAt)}. If you change
        your mind before then, sign in to the Tribys app to restore it. You can
        close this page now.
      </p>
    </div>;
}
const confirmDeletion = async ({
  deleteAccount,
  onDeleted
}: {
  deleteAccount: () => Promise<{
    data?: ResultOf<typeof DELETE_ACCOUNT> | null;
  }>;
  onDeleted: (deleteAt: string) => void;
}) => {
  const result = await deleteAccount().catch(() => null);
  if (result?.data) onDeleted(result.data.deleteAccount.deleteAt ?? new Date().toISOString());
};
const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});